/**
 * Chat Storage Utility
 * Manages localStorage persistence for conversation history
 */

const STORAGE_PREFIX = 'natalie-chat-';
const MAX_MESSAGES = 50;
const EXPIRY_DAYS = 7;
const SCHEMA_VERSION = 1;

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
export function isStorageAvailable() {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Generate a unique message ID
 * @returns {string}
 */
export function generateMessageId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a message object
 * @param {string} role - 'user' or 'bot'
 * @param {string} content - Message content
 * @param {string} type - 'analysis', 'follow-up', or 'quick-action'
 * @returns {Object}
 */
export function createMessage(role, content, type = 'follow-up') {
  return {
    id: generateMessageId(),
    role,
    content,
    timestamp: Date.now(),
    type
  };
}

/**
 * Get chat history from localStorage
 * @param {number|string} ticketId
 * @returns {Object|null} - { version, ticketId, conversationId, messages, createdAt, lastUpdatedAt }
 */
export function getChatHistory(ticketId) {
  if (!isStorageAvailable() || !ticketId) return null;

  const key = `${STORAGE_PREFIX}${ticketId}`;
  const data = localStorage.getItem(key);

  if (!data) return null;

  try {
    const parsed = JSON.parse(data);

    // Check if expired
    const now = Date.now();
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (now - parsed.lastUpdatedAt > expiryMs) {
      clearChatHistory(ticketId);
      return null;
    }

    // Handle schema migrations if needed
    if (parsed.version !== SCHEMA_VERSION) {
      // For now, just clear old schema versions
      clearChatHistory(ticketId);
      return null;
    }

    return parsed;
  } catch {
    // Corrupted data, clear it
    clearChatHistory(ticketId);
    return null;
  }
}

/**
 * Save chat history to localStorage
 * @param {number|string} ticketId
 * @param {string} conversationId
 * @param {Array} messages
 * @returns {boolean} - Success status
 */
export function saveChatHistory(ticketId, conversationId, messages) {
  if (!isStorageAvailable() || !ticketId) return false;

  const key = `${STORAGE_PREFIX}${ticketId}`;

  // Trim messages to max limit (keep newest)
  const trimmedMessages = messages.slice(-MAX_MESSAGES);

  // Get existing data to preserve createdAt
  const existing = getChatHistory(ticketId);

  const data = {
    version: SCHEMA_VERSION,
    ticketId,
    conversationId,
    messages: trimmedMessages,
    createdAt: existing?.createdAt || Date.now(),
    lastUpdatedAt: Date.now()
  };

  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    // Storage quota exceeded or other error
    console.warn('Failed to save chat history:', error);
    return false;
  }
}

/**
 * Clear chat history for a specific ticket
 * @param {number|string} ticketId
 */
export function clearChatHistory(ticketId) {
  if (!isStorageAvailable() || !ticketId) return;

  const key = `${STORAGE_PREFIX}${ticketId}`;
  localStorage.removeItem(key);
}

/**
 * Clean up all expired chat histories
 * Called on app initialization
 */
export function cleanupExpiredHistory() {
  if (!isStorageAvailable()) return;

  const now = Date.now();
  const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (now - data.lastUpdatedAt > expiryMs) {
          keysToRemove.push(key);
        }
      } catch {
        // Corrupted data, remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));

  if (keysToRemove.length > 0) {
    console.log(`Cleaned up ${keysToRemove.length} expired chat histories`);
  }
}
